import {
  actionColumn,
  booleanFilter,
  Button,
  collapseColumn,
  column,
  Css,
  dateColumn,
  emptyCell,
  FilterDefs,
  Filters,
  GridDataRow,
  GridSortConfig,
  GridTable,
  GridTableApi,
  multiFilter,
  Palette,
  RightPaneLayout,
  RowStyles,
  ScrollableContent,
  selectColumn,
  simpleHeader,
  Tag,
  toggleFilter,
  useComputed,
  useGridTableApi,
  useGroupBy,
  useModal,
  usePersistedFilter,
  useRightPane,
  useSnackbar,
  useTestIds,
} from "@homebound/beam";
import { useEffect, useMemo, useState } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { dateCell, FileUploadProgressAlert, SearchBox, UppyUploader } from "src/components";
import { Icon } from "src/components/Icon";
import { AssetUppyFile } from "src/components/uploader/UppyUploader";
import { UploaderProvider } from "src/contexts/UploaderContext";
import {
  DocumentDetailDocumentTypeFragment,
  NamedDevelopmentCohortsProjectsFragment,
} from "src/fragments/circularFragments.generated";
import {
  DocumentCategory,
  DocumentCategoryDetail,
  DocumentsFilter,
  DocumentType,
  Project,
  SaveDocumentInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { PageHeader } from "src/routes/layout/PageHeader";
import { TableActions } from "src/routes/layout/TableActions";
import { DocumentsActionsMenu } from "src/routes/projects/documents/DocumentsActionsMenu";
import {
  DocumentsPageDocumentDetailFragment,
  DocumentsPageDocumentParentFragment,
  DocumentsPageProjectDetailFragment,
  useDocumentsPageMetadataQuery,
  useDocumentsPageQuery,
  useSaveDocumentsMutation,
  useToggleDocumentAsFavoritedMutation,
} from "src/routes/projects/documents/DocumentsPage.generated";
import { UpdateGDriveModal } from "src/routes/projects/documents/UpdateGDriveModal";
import { createDocumentsUrl, DocumentsParams } from "src/RouteUrls";
import {
  formatNumberToString,
  isDefined,
  isEmpty,
  nonEmpty,
  partition,
  pushInNextLoop,
  safeEntries,
  sortBy,
} from "src/utils";
import { getGoogleDriveLink } from "src/utils/googleDrive";
import { isProjectType } from "src/utils/projects";
import { queryResult } from "src/utils/queryResult";
import { zIndexes } from "src/utils/styles/zIndexes";
import { StringParam, useQueryParams } from "use-query-params";
import { DocumentEditor } from "./DocumentEditor";

type HeaderRow = { kind: "header" };
type DocumentRow = { kind: "document"; data: DocumentsPageDocumentDetailFragment };
type GroupedByRow = { kind: "group"; data: { category?: string; project?: string; childCount?: number } };
type Row = HeaderRow | DocumentRow | GroupedByRow;

export const DOCUMENT_LIMIT = 500;

type DocumentsPageProps = { readyPlanId?: string; versionId?: string };

export enum DocumentsPageMode {
  Development = "development",
  ReadyPlan = "readyPlan",
  Project = "project",
  Hierarchy = "hierarchy",
}

export function DocumentsPage(props: DocumentsPageProps) {
  const { projectId, developmentId } = useRouteParams<{ projectId?: string; developmentId?: string }>();
  const { readyPlanId, versionId } = props;
  const parentId = readyPlanId ?? developmentId ?? projectId!;
  const metaDataQuery = useDocumentsPageMetadataQuery({
    variables: { parentId, skipDevelopment: !isDefined(developmentId), skipProject: !isDefined(projectId) },
  });
  const mode = isDefined(readyPlanId)
    ? DocumentsPageMode.ReadyPlan
    : isDefined(developmentId)
      ? DocumentsPageMode.Development
      : DocumentsPageMode.Project;

  return queryResult(metaDataQuery, {
    data: ({ documentTypes, documentCategories, development, project }) => (
      <DocumentsDataView
        mode={project && project.childrenRecursive.nonEmpty ? DocumentsPageMode.Hierarchy : mode}
        parentId={parentId}
        versionId={versionId}
        documentTypes={documentTypes.sortByKey("name")}
        documentCategories={documentCategories.sortByKey("name")}
        development={development}
        project={project}
      />
    ),
  });
}

type GroupBy = "category" | "project" | "none";

type DocumentsDataViewProps = {
  mode: DocumentsPageMode;
  parentId: string;
  versionId?: string;
  documentTypes: DocumentDetailDocumentTypeFragment[];
  documentCategories: DocumentCategoryDetail[];
  development?: NamedDevelopmentCohortsProjectsFragment;
  project?: DocumentsPageProjectDetailFragment;
};

function DocumentsDataView(props: DocumentsDataViewProps) {
  const { mode, documentTypes, documentCategories, development, parentId, versionId, project } = props;
  const tid = useTestIds({}, "documentsPage");
  const parentProject = mode === DocumentsPageMode.Hierarchy ? project : undefined;
  const deSelectedCategories = [
    DocumentCategory.Accounting,
    DocumentCategory.Contractual,
    DocumentCategory.Procurement,
  ] as DocumentCategory[];

  const filterDefs: FilterDefs<DocumentsFilter> = useMemo(
    () => {
      const category = multiFilter({
        label: "Category",
        options: sortBy(documentCategories, (category) => category.name),
        defaultValue: documentCategories
          .filter((category) => !deSelectedCategories.includes(category.code))
          .map((category) => category.code),
        getOptionValue: (o) => o.code,
        getOptionLabel: (o) => o.name,
      });

      const homeownerVisible = booleanFilter({
        label: "Visibility",
        options: [
          [undefined, "All"],
          [true, "Homeowner"],
          [false, "Internal Only"],
        ],
      });

      const label = mode === DocumentsPageMode.ReadyPlan ? "Show Removed" : "Show Archived";
      const includeDeleted = toggleFilter({ label });

      return { category, ...(mode === DocumentsPageMode.ReadyPlan ? {} : { homeownerVisible }), includeDeleted };
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentTypes, parentId, versionId],
  );
  const groupByOptions = { category: "Category", project: "Project", none: "None" };
  if (mode !== DocumentsPageMode.Development && mode !== DocumentsPageMode.Hierarchy) {
    delete (groupByOptions as any).project;
  }

  const groupBy = useGroupBy<GroupBy>(groupByOptions);
  const { setFilter, filter } = usePersistedFilter<DocumentsFilter>({
    storageKey: `${parentId}_documentsFilter`,
    filterDefs,
  });
  const { openModal } = useModal();
  const { triggerNotice } = useSnackbar();

  const projects = isDefined(project)
    ? [project, ...project.childrenRecursive]
    : (development?.cohorts?.flatMap((c) => c.projects) ?? []);

  const parentIds = [development?.id, ...projects.map((p) => p.id)].compact();

  const query = useDocumentsPageQuery({
    variables: {
      filter: {
        ...filter,
        ...(mode === DocumentsPageMode.ReadyPlan ? { readyPlanId: parentId, versionId } : { parentIds }),
      },
      page: { limit: DOCUMENT_LIMIT },
    },
    skip: isEmpty(parentIds) && !isDefined(parentId),
  });

  // Key on the query result's value, not the reference Apollo re-creates on unrelated cache
  // writes, so the truncation warning only re-fires when the fetched documents actually change.
  const documentsKey = useMemo(() => JSON.stringify(query.data), [query.data]);
  useEffect(() => {
    if (query.data?.documents.pageInfo.hasNextPage) {
      triggerNotice({
        icon: "warning",
        message: `This list has been truncated to ${formatNumberToString(DOCUMENT_LIMIT, true)} documents, please add more filters.`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentsKey, triggerNotice]);

  const [searchFilter, setSearchFilter] = useState<string | undefined>();
  const [allowDocRedirect, setAllowDocRedirect] = useState(true);

  const [{ documentId }] = useQueryParams({ documentId: StringParam });
  const [saveDocuments] = useSaveDocumentsMutation();
  const navigate = useNavigate();

  const { openRightPane, closeRightPane } = useRightPane();
  const [newVersionId, setNewVersionId] = useState<string | undefined>();

  useEffect(() => {
    // After uploading a new document, we go through a loop where `documentId` is updated but `versionId` is not. This
    // then fires a query for a document in a version it doesn't for, which results in a backend error. So when we
    // transition to a new draft, we store the version we are transitioning to and wait until it's populated here to
    // display the document editor pane
    documentId && (mode !== DocumentsPageMode.ReadyPlan || newVersionId === undefined || newVersionId === versionId)
      ? openRightPane({
          content: (
            <DocumentEditor
              documentId={documentId}
              groupBy={groupBy.value}
              documentTypes={documentTypes}
              versionId={versionId}
            />
          ),
        })
      : closeRightPane();
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, versionId, newVersionId]);

  useEffect(() => {
    if (newVersionId === versionId) setNewVersionId(undefined);
  }, [versionId, newVersionId, setNewVersionId]);

  // Once an asset has been uploaded, create a document with an asset
  async function handleOnFinish(file: AssetUppyFile) {
    // This is to prevent multiple file uploads to redirect one after each other
    if (allowDocRedirect) {
      setAllowDocRedirect(false);
    }
    const saveDocumentsInput: [SaveDocumentInput] = [
      {
        documentType: DocumentType.General,
        name: file.name,
        sizeInBytes: file.size,
        parentId: parentId,
        asset: {
          contentType: file.type,
          fileName: file.name,
          s3Key: file.meta.s3Key as string,
          sizeInBytes: file.size,
        },
      },
    ];
    const { data } = await saveDocuments({
      variables: { input: saveDocumentsInput },
      refetchQueries: ["DocumentsPage"],
    });

    const savedDoc = data?.saveDocuments.documents[0];
    if (savedDoc && allowDocRedirect) {
      const versionId = savedDoc.parent.__typename === "PlanPackage" ? savedDoc.parent.aggregateDraft?.id : undefined;
      setNewVersionId(versionId);
      const url = createDocumentsUrl(parentId, { versionId, groupBy: groupBy.value, documentId: savedDoc.id });
      navigate(url);
    }
  }

  const tableApi = useGridTableApi<Row>();

  const selectedDocuments: DocumentsPageDocumentDetailFragment[] = useComputed(
    () => tableApi.getSelectedRows("document").map((row) => row.data),
    [tableApi],
  );

  return queryResult(query, {
    data: ({ documents }) => {
      return (
        <div {...tid}>
          <UploaderProvider>
            <PageHeader
              xss={Css.bgWhite.$}
              title="Documents"
              right={
                isDefined(project) && (
                  <>
                    <Button
                      label="Edit GDrive Link"
                      variant="secondary"
                      icon="pencil"
                      onClick={() =>
                        openModal({
                          content: (
                            <UpdateGDriveModal
                              projectId={parentId}
                              googleDriveLink={getGoogleDriveLink(project.googleDriveFolderId || "")}
                              refetch={query.refetch}
                            />
                          ),
                        })
                      }
                    />
                    {project.googleDriveFolderId && (
                      <Button label="Google Drive" onClick={getGoogleDriveLink(project.googleDriveFolderId)} />
                    )}
                  </>
                )
              }
            />
            <TableActions>
              <Filters groupBy={groupBy} filter={filter} onChange={setFilter} filterDefs={filterDefs} />
              <div css={Css.df.aic.gap2.$}>
                <SearchBox onSearch={setSearchFilter} />
                <DocumentsActionsMenu
                  mode={mode}
                  versionId={versionId}
                  selectedDocuments={selectedDocuments}
                  documentTypes={documentTypes}
                  tableRefetch={query.refetch}
                  tableApi={tableApi}
                  development={development}
                  parentProject={parentProject}
                />
                <UppyUploader onFinish={handleOnFinish} onComplete={() => setAllowDocRedirect(true)} />
              </div>
            </TableActions>
            <ScrollableContent omitBottomPadding>
              <RightPaneLayout>
                <>
                  <DocumentsTable
                    mode={mode}
                    documents={documents.entities}
                    parentId={parentId}
                    versionId={versionId}
                    projects={projects}
                    searchFilter={searchFilter}
                    selectedDocumentId={documentId}
                    tableApi={tableApi}
                    documentCategories={documentCategories}
                    groupBy={groupBy.value}
                  />
                  <div
                    css={Css.fixed.bottom2.w50.add("zIndex", zIndexes.modal).$}
                    data-testid="documentsPage_fileUploader"
                  >
                    <FileUploadProgressAlert />
                  </div>
                </>
              </RightPaneLayout>
            </ScrollableContent>
          </UploaderProvider>
        </div>
      );
    },
  });
}

type DocumentsTableProps = {
  mode: DocumentsPageMode;
  documents: DocumentsPageDocumentDetailFragment[];
  parentId: string;
  versionId?: string;
  projects: Pick<Project, "id" | "name">[];
  searchFilter?: string;
  selectedDocumentId: string | undefined | null;
  tableApi: GridTableApi<Row>;
  documentCategories: DocumentCategoryDetail[];
  groupBy: GroupBy;
};

function DocumentsTable(props: DocumentsTableProps) {
  const {
    mode,
    documents,
    projects,
    parentId,
    versionId,
    searchFilter = "",
    selectedDocumentId,
    tableApi,
    documentCategories,
    groupBy,
  } = props;

  const [toggleDocumentAsFavoritedMutation] = useToggleDocumentAsFavoritedMutation();

  // GroupBy is not store in the filters so we need set it again
  const rowStyles: RowStyles<Row> = useMemo(
    () => ({
      header: {},
      document: { rowLink: ({ data }) => createDocumentsUrl(parentId, { versionId, groupBy, documentId: data.id }) },
    }),
    [versionId, parentId, groupBy],
  );

  const rows = useMemo(
    () => (documents ? createRows(documents, groupBy, parentId) : []),
    [documents, groupBy, parentId],
  );
  const columns = useMemo(
    () => {
      const categoryColumn = column<Row>({
        header: "Category",
        document: (document) => documentCategories?.find((d) => d.code === document.category)?.name ?? " ",
        group: (data) => {
          const { category, childCount } = data;
          return {
            content: () =>
              category ? (
                <>
                  <div css={Css.mr1.$}>
                    {documentCategories?.find((d) => d.code === category)?.name ?? "No Category"}
                  </div>
                  {childCount && childCount > 0 && <Tag text={childCount.toString()} />}
                </>
              ) : (
                ""
              ),
            sortValue: data.category,
            colspan: 7,
          };
        },
      });
      const favoriteColumn = actionColumn<Row>({
        id: "favorite",
        header: emptyCell,
        document: (document) => ({
          content: (
            <Icon
              icon={document.isFavorited ? "starFilled" : "star"}
              color={document.isFavorited ? Palette.Yellow500 : Palette.Gray900}
              onClick={() => toggleDocumentAsFavoritedMutation({ variables: { input: { documentId: document.id } } })}
            />
          ),
          sortValue: document.isFavorited,
        }),
        w: "45px",
        group: emptyCell,
        clientSideSort: false,
      });
      const nameColumn = column<Row>({
        header: "Name",
        document: (document) => ({ content: document.displayName, value: document.displayName.toLowerCase() }),
        mw: "300px",
        group: emptyCell,
      });
      const typeColumn = column<Row>({
        header: "Type",
        document: ({ documentType: { name, code } }) =>
          code === DocumentType.General ? { content: () => <Icon icon="error" />, value: undefined } : name,
        group: emptyCell,
      });
      const dateIssuanceColumn = dateColumn<Row>({
        header: "Issuance Date",
        document: (document) => dateCell(document.issuanceDate),
        w: "95px",
        group: emptyCell,
      });
      const hoVisibleColumn = column<Row>({
        header: "H/O Visibile",
        document: ({ homeownerVisible }) => (homeownerVisible ? "Yes" : "No"),
        w: "80px",
        group: emptyCell,
      });
      const archiveColumn = column<Row>({
        header: "  ",
        document: ({ isDeleted }) => (isDeleted ? <Icon icon={"archive"} /> : " "),
        w: "45px",
        clientSideSort: false,
        group: emptyCell,
      });
      const projectColumn = column<Row>({
        header: "Project",
        document: ({ parent }) => (isProjectType(parent) ? projects.find(({ id }) => id === parent?.id)?.name : "--"),
        mw: "160px",
        group: (data) => {
          const { project, childCount } = data;
          return {
            content: () =>
              project ? (
                <>
                  <div css={Css.mr1.$}>{project}</div>
                  {childCount && childCount > 0 && <Tag text={childCount.toString()} />}
                </>
              ) : (
                <></>
              ),
            sortValue: data.project,
            colspan: 7,
          };
        },
      });

      return [
        collapseColumn<Row>(),
        selectColumn<Row>(),
        favoriteColumn,
        ...(groupBy === "category" ? [categoryColumn] : []),
        ...(groupBy === "project" ? [projectColumn] : []),
        nameColumn,
        typeColumn,
        ...(groupBy !== "category" ? [categoryColumn] : []),
        ...(mode === DocumentsPageMode.ReadyPlan ? [] : [dateIssuanceColumn, hoVisibleColumn]),
        ...((mode === DocumentsPageMode.Development || mode === DocumentsPageMode.Hierarchy) && groupBy !== "project"
          ? [projectColumn]
          : []),
        archiveColumn,
      ];
    },
    // TODO: validate this eslint-disable. It was automatically ignored as part of https://app.shortcut.com/homebound-team/story/40033/enable-react-hooks-exhaustive-deps-for-internal-frontend
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentCategories, groupBy, mode],
  );

  // Find is setting the primary column to favoriteColumn
  const sortingConfig: GridSortConfig = { on: "client", primary: ["favorite", "DESC"] };

  return (
    <GridTable
      as="virtual"
      columns={columns}
      rows={rows}
      rowStyles={rowStyles}
      sorting={sortingConfig}
      stickyHeader
      filter={searchFilter}
      style={{ rowHeight: "fixed", grouped: groupBy !== "none", bordered: true, allWhite: true }}
      activeRowId={selectedDocumentId ? `document_${selectedDocumentId}` : undefined}
      api={tableApi}
      fallbackMessage="No documents found"
    />
  );
}

function createRows(documents: DocumentsPageDocumentDetailFragment[], groupBy: GroupBy, parentId: string) {
  const createDocumentRow = (doc: DocumentsPageDocumentDetailFragment) => ({
    kind: "document" as const,
    id: doc.id,
    data: doc,
  });
  const kind = "group" as const;
  if (groupBy === "category") {
    const [withCategory, noCategory] = partition(documents, (d) => !!d.category);
    const groups = withCategory.groupBy((doc) => doc.category!);
    const rows = safeEntries(groups).map(([groupName, docList]) => ({
      kind,
      id: groupName,
      data: { category: groupName, childCount: docList.length },
      children: docList.map(createDocumentRow),
    }));
    return [
      simpleHeader,
      ...rows,
      ...(nonEmpty(noCategory)
        ? [
            {
              kind,
              id: "No Category",
              data: { category: "No Category", childCount: noCategory.length },
              children: noCategory.map(createDocumentRow),
              fixedSort: "last" as const,
            },
          ]
        : []),
    ];
  } else if (groupBy === "project") {
    const [parentLevelDocuments, projectDocuments] = documents.partition((d) => d.parent.id === parentId);
    const parentLabel = parentLevelDocuments.first?.parent.__typename ?? "Development";
    return [
      simpleHeader,
      ...(projectDocuments.nonEmpty
        ? [
            {
              kind,
              id: "Project Level",
              data: { project: "Project Level", childCount: projectDocuments.length },
              children: projectDocuments.map(createDocumentRow),
            },
          ]
        : []),
      ...(parentLevelDocuments.nonEmpty
        ? [
            {
              kind,
              id: `${parentLabel} Level`,
              data: { project: `${parentLabel} Level`, childCount: parentLevelDocuments.length },
              children: parentLevelDocuments.map(createDocumentRow),
              fixedSort: "last" as const,
            },
          ]
        : []),
    ] satisfies GridDataRow<Row>[];
  } else {
    return [simpleHeader, ...documents.map(createDocumentRow)];
  }
}

// If we are modifying ready plan documents, then our changes may create a new draft. If so, then we need to navigate
// to that draft to display the changes we made.
export function maybeNavigateToNewDraft(
  navigate: NavigateFunction,
  versionId: string | undefined,
  parents: DocumentsPageDocumentParentFragment[],
  params: DocumentsParams = {},
): boolean {
  if (versionId) {
    const parent = parents.find((p) => p.__typename === "PlanPackage" && p.aggregateDraft?.id !== versionId);
    if (parent?.__typename === "PlanPackage") {
      const url = createDocumentsUrl(parent.id, { versionId: parent.aggregateDraft?.id, ...params });
      pushInNextLoop(navigate, url);
      return true;
    }
  }
  return false;
}
